import { useState, useEffect, useRef } from "react";
import { X, Send, MessageCircle, Minimize2 } from "lucide-react";
import { findBestResponse, defaultResponse } from "./ChatBotKnowledgeBase";

interface Message {
  id: number;
  text: string;
  sender: "user" | "bot";
  timestamp: Date;
  quickOptions?: QuickOption[];
}

interface QuickOption {
  label: string;
  value: string;
  icon: string;
}

const QUICK_OPTIONS: QuickOption[] = [
  { label: "Soporte Técnico", value: "necesito soporte tecnico", icon: "🛠️" },
  { label: "Problemas Alojamiento", value: "tengo problemas con mi alojamiento", icon: "🏠" },
  { label: "Cancelar Reserva", value: "como cancelo mi reserva", icon: "❌" },
  { label: "Optimizar Anuncio", value: "como optimizo mi anuncio", icon: "⭐" },
  { label: "Crear Anuncio", value: "como creo un anuncio", icon: "📝" },
  { label: "Políticas", value: "necesito informacion sobre politicas", icon: "📋" },
  { label: "WhatsApp", value: "whatsapp", icon: "💬" },
  { label: "Ayuda General", value: "necesito ayuda general", icon: "💡" },
];

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [showQuickOptions, setShowQuickOptions] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Mensaje de bienvenida al abrir el chat
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setTimeout(() => {
        addBotMessage(
          "¡Hola! 👋 Soy el asistente virtual de Rumbo Punilla.\n\n¿En qué puedo ayudarte hoy?",
          QUICK_OPTIONS
        );
        setShowQuickOptions(true);
      }, 500);
    }
  }, [isOpen]);

  // Scroll automático al final
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus en input al abrir
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const addBotMessage = (text: string, options?: QuickOption[]) => {
    const newMessage: Message = {
      id: Date.now(),
      text,
      sender: "bot",
      timestamp: new Date(),
      quickOptions: options,
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  const handleSend = (message?: string) => {
    const textToSend = message || inputValue.trim();
    if (!textToSend) return;

    // Ocultar opciones rápidas cuando el usuario envía un mensaje
    setShowQuickOptions(false);

    // Agregar mensaje del usuario
    const userMessage: Message = {
      id: Date.now(),
      text: textToSend,
      sender: "user",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");

    // Simular "escribiendo..."
    setIsTyping(true);

    // Buscar respuesta en la base de conocimiento
    setTimeout(() => {
      const response = findBestResponse(textToSend) || defaultResponse;
      setIsTyping(false);
      addBotMessage(response.response);

      // Agregar preguntas de seguimiento si existen
      if (response.followUp && response.followUp.length > 0) {
        setTimeout(() => {
          addBotMessage(
            "También puedes preguntarme:\n" +
              response.followUp!.map((q, i) => `${i + 1}. ${q}`).join("\n")
          );
        }, 1000);
      }
    }, 800 + Math.random() * 700); // Tiempo de respuesta variable
  };

  const handleQuickOptionClick = (value: string) => {
    // Si es WhatsApp, abrir directamente en nueva ventana
    if (value === "whatsapp") {
      window.open("http://wa.me/5493516837321", "_blank");
      return;
    }
    handleSend(value);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      {/* Botón flotante */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="fixed lg:bottom-6 bottom-[60px] right-4 lg:right-6 z-[2] bg-gradient-to-r from-[#8B9456] to-[#7A8249] text-white rounded-full w-16 h-16 flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300 hover:shadow-[0_0_30px_rgba(139,148,86,0.6)] group"
          aria-label="Abrir chat de ayuda"
        >
          <MessageCircle className="w-7 h-7 group-hover:animate-bounce" />
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-6 h-6 rounded-full flex items-center justify-center animate-pulse">
          </span>
        </button>
      )}

      {/* Ventana del chat */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-[2] w-[380px] h-[600px] max-w-[calc(100vw-3rem)] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 animate-slideUp">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#8B9456] to-[#7A8249] text-white px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Asistente Rumbo Punilla</h3>
                <p className="text-xs text-white/80 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  En línea
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={toggleChat}
                className="hover:bg-white/20 p-2 rounded-full transition-colors"
                aria-label="Minimizar chat"
              >
                <Minimize2 className="w-5 h-5" />
              </button>
              <button
                onClick={toggleChat}
                className="hover:bg-white/20 p-2 rounded-full transition-colors"
                aria-label="Cerrar chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Mensajes */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message) => (
              <div key={message.id}>
                <div
                  className={`flex ${
                    message.sender === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                      message.sender === "user"
                        ? "bg-[#8B9456] text-white rounded-br-sm"
                        : "bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-line leading-relaxed">
                      {message.text}
                    </p>
                    <p
                      className={`text-[10px] mt-1 ${
                        message.sender === "user"
                          ? "text-white/70"
                          : "text-gray-400"
                      }`}
                    >
                      {message.timestamp.toLocaleTimeString("es-AR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>

                {/* Opciones rápidas */}
                {message.quickOptions && showQuickOptions && (
                  <div className="mt-3 grid grid-cols-2 gap-2 max-w-[90%]">
                    {message.quickOptions.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuickOptionClick(option.value)}
                        className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-[#8B9456] hover:text-white text-gray-700 rounded-lg text-xs font-medium transition-all duration-200 shadow-sm border border-gray-200 hover:border-[#8B9456] hover:shadow-md group"
                      >
                        <span className="text-base group-hover:scale-110 transition-transform">
                          {option.icon}
                        </span>
                        <span className="text-left leading-tight">
                          {option.label}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Indicador de "escribiendo..." */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm border border-gray-100">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                    <span
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    ></span>
                    <span
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.4s" }}
                    ></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Escribe tu pregunta..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#8B9456] focus:border-transparent text-sm"
                disabled={isTyping}
              />
              <button
                onClick={() => handleSend()}
                disabled={!inputValue.trim() || isTyping}
                className="bg-[#8B9456] text-white p-3 rounded-full hover:bg-[#7A8249] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                aria-label="Enviar mensaje"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-2 text-center">
              Respuestas automáticas basadas en nuestra documentación
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
